import type { ThreekitEmbedHandle } from "./embed-api";

const STYLE_ID = "tk-ar-dock-styles";

function ensureArDockStyles(): void {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = `
.tk-ar-dock__btn {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: none;
  background: #55606b;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.25);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  z-index: 20;
  transition: background 0.15s;
}
.tk-ar-dock__btn:hover {
  background: #47515a;
}
.tk-ar-dock__btn:focus-visible {
  outline: 2px solid #1a56db;
  outline-offset: 2px;
}
.tk-ar-dock__btn:disabled {
  opacity: 0.6;
  cursor: default;
}
.tk-ar-dock__icon-text {
  font: 700 15px/1 system-ui, sans-serif;
  color: #fff;
  letter-spacing: 0.3px;
}
.tk-ar-modal {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}
.tk-ar-modal__panel {
  position: relative;
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  max-width: 90vw;
}
.tk-ar-modal__title {
  margin: 0;
  font: 600 16px/1.3 system-ui, sans-serif;
  color: #1a1a1a;
}
.tk-ar-modal__qr {
  width: 220px;
  height: 220px;
  display: block;
}
.tk-ar-modal__close {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: #555;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
}
.tk-ar-modal__close:hover {
  background: rgba(0, 0, 0, 0.08);
}
`;
  document.head.appendChild(style);
}

export interface ArLaunchDockOptions {
  /** Threekit env subdomain (e.g. `preview`), sent to the QR endpoint as `environment`. */
  env: string;
  /**
   * Org id the player was mounted with, sent to the QR endpoint as `orgId`. Together with `env`
   * it selects the environment (dev / staging / prod) whose public token the AR landing page
   * uses — required to tell staging and prod apart, since both run on `admin-fts`.
   */
  orgId?: string;
  /** Catalog item id; defaults to `embed.productAssetId`. */
  assetId?: string;
  /**
   * Same-origin path (or absolute https URL, for direct CPQ embed where this script runs on
   * CPQ's own document) to the AR QR endpoint. Default `/api/ar/qr`.
   */
  qrEndpoint?: string;
  /** Image format the QR endpoint returns (default `svg`). */
  format?: "svg" | "png";
  /** Merged into `player.saveConfiguration({ metadata })` (default `{ source: "pride-cpq-ar" }`). */
  metadata?: Record<string, unknown>;
}

export interface ArLaunchDockHandle {
  detach(): void;
}

type PlayerWithSaveConfiguration = {
  saveConfiguration?: (opts?: {
    metadata?: Record<string, unknown>;
  }) => { shortId: string } | Promise<{ shortId: string }>;
};

/**
 * Resolves a same-origin path against `window.location.origin`; passes absolute URLs through.
 * Needed because in direct CPQ embed mode this script runs on CPQ's own document, so a plain
 * relative path would resolve against CPQ's origin, not this app's.
 */
export function resolveArEndpoint(path: string): string {
  if (/^https?:\/\//i.test(path)) return path;
  const rel = path.startsWith("/") ? path : `/${path}`;
  return `${window.location.origin}${rel}`;
}

/**
 * Overlays a "View in AR" button (top-right of the player shell). On click, saves the current
 * configuration ({@link https://developer.threekit.com/reference/save-configuration}) and shows
 * a modal with a QR code (generated server-side, see `server/ar-service.mjs`) that points a
 * phone at the unauthenticated `/ar` landing page for this saved configuration.
 *
 * Pass `showAR: false` at `mount()` (the default) so Threekit's native AR button never appears —
 * this dock fully owns the AR entry point. See docs/AR-HOSTING-SPEC.md.
 */
export function attachArLaunchDock(
  embed: ThreekitEmbedHandle,
  shell: HTMLElement,
  options: ArLaunchDockOptions
): ArLaunchDockHandle {
  ensureArDockStyles();

  const qrEndpoint = resolveArEndpoint(options.qrEndpoint?.trim() || "/api/ar/qr");
  const format = options.format ?? "svg";

  if (getComputedStyle(shell).position === "static") {
    shell.style.position = "relative";
  }

  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = "tk-ar-dock__btn";
  btn.title = "View in AR";
  btn.setAttribute("aria-label", "View in AR");
  btn.innerHTML = `<span class="tk-ar-dock__icon-text" aria-hidden="true">AR</span>`;

  let overlay: HTMLElement | null = null;

  function closeOverlay(): void {
    overlay?.remove();
    overlay = null;
  }

  function showOverlay(shortId: string, assetId: string): void {
    closeOverlay();
    const params = new URLSearchParams({
      shortId,
      assetId,
      environment: options.env,
      format,
    });
    if (options.orgId) params.set("orgId", options.orgId);
    const src = `${qrEndpoint}?${params.toString()}`;

    const wrap = document.createElement("div");
    wrap.className = "tk-ar-modal";
    wrap.innerHTML = `
      <div class="tk-ar-modal__panel">
        <button type="button" class="tk-ar-modal__close" aria-label="Close">&times;</button>
        <p class="tk-ar-modal__title">Scan to view in AR</p>
        <img class="tk-ar-modal__qr" src="${src}" alt="QR code to view this configuration in AR on your phone" />
      </div>
    `;
    wrap.addEventListener("click", (e) => {
      if (e.target === wrap) closeOverlay();
    });
    wrap
      .querySelector(".tk-ar-modal__close")
      ?.addEventListener("click", closeOverlay);
    document.addEventListener("keydown", function onKeydown(e) {
      if (e.key === "Escape") {
        closeOverlay();
        document.removeEventListener("keydown", onKeydown);
      }
    });
    document.body.appendChild(wrap);
    overlay = wrap;
  }

  btn.addEventListener("click", () => {
    const assetId = options.assetId ?? embed.productAssetId;
    if (!assetId) {
      console.error("[ar-launch-dock] no assetId available for AR QR code");
      return;
    }
    const player = embed.player as PlayerWithSaveConfiguration;
    if (typeof player.saveConfiguration !== "function") {
      console.error(
        "[ar-launch-dock] player.saveConfiguration is not available (Threekit player version)"
      );
      return;
    }
    btn.disabled = true;
    Promise.resolve(
      player.saveConfiguration({
        metadata: options.metadata ?? { source: "pride-cpq-ar" },
      })
    )
      .then(({ shortId }) => {
        showOverlay(shortId, assetId);
      })
      .catch((err: unknown) => {
        console.error("[ar-launch-dock] saveConfiguration failed", err);
      })
      .finally(() => {
        btn.disabled = false;
      });
  });

  shell.appendChild(btn);

  return {
    detach() {
      closeOverlay();
      btn.remove();
    },
  };
}
